<template>
  <div>
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-loading text="加载中..." background="rgba(255,255,255,0.8)" />
    </div>
    
    <!-- 主内容区 -->
    <el-main v-show="!loading && course.id" class="container">
      <!-- 视频播放区 -->
      <div class="video-container">
        <!-- 视频播放器 -->
        <div class="video-player">
          <video controls width="100%" height="auto" :src="processedVideoUrl">
            您的浏览器不支持 HTML5 视频播放
          </video>
          <!-- 视频URL调试信息 -->
          <div class="video-debug" v-if="showDebug">
            <p>视频URL: {{ processedVideoUrl }}</p>
          </div>
        </div>
        <!-- 视频信息 -->
        <div class="video-info">
          <h1 class="video-title">{{ course.title }}</h1>
          <div class="teacher-info">
            <div class="teacher-avatar">{{ course.teachername?.charAt(0) || '?' }}</div>
            <div class="teacher-details">
              <h3>{{ course.teachername }}</h3>
              <p>讲师</p>
            </div>
          </div>
          <div class="video-description">
            <p v-html="course.content"></p>
          </div>
        </div>
      </div>
    </el-main>
    
    <!-- 空状态 -->
    <div v-show="!loading && !course.id" class="empty-state">
      <p>课程不存在或已删除</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { getOnlineClassDetail } from "@/api/processApi";
import { baseURL } from "@/api/axiosConfig";

// 获取路由信息
const route = useRoute();
// 初始化课程信息
const course = ref({});
// 加载状态
const loading = ref(true);
// 是否显示调试信息
const showDebug = ref(true);

// 处理后的视频URL（响应式）
const processedVideoUrl = computed(() => {
  const videoUrl = course.value.videoUrl;
  if (!videoUrl) {
    console.log("视频URL为空");
    return "";
  }
  // 去除前后的反引号、空格和可能的引号
  let url = videoUrl.trim().replace(/^[`"'\\s]+|[`"'\\s]+$/g, '');
  
  // 如果已经是完整的HTTP URL，直接返回
  if (url.startsWith("http")) {
    return url;
  }
  
  // 否则拼接视频服务器地址
  const finalUrl = baseURL + url;
  console.log("处理后的视频URL:", finalUrl);
  return finalUrl;
});

// 根据课程 ID 加载课程信息
const loadCourse = async (id) => {
  loading.value = true;
  try {
    const numericId = parseInt(id);
    console.log("加载课程详情, ID:", numericId);
    const response = await getOnlineClassDetail(numericId);
    console.log("接口响应:", response);
    if (response.data.code === 200) {
      course.value = response.data.data;
      console.log("课程数据:", course.value);
      console.log("原始videoUrl:", course.value.videoUrl);
      console.log("处理后videoUrl:", processedVideoUrl.value);
    } else {
      console.error("获取课程详情失败:", response.data.msg);
    }
  } catch (error) {
    console.error("获取课程详情失败:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  const courseId = route.params.id;
  console.log("路由参数ID:", courseId);
  loadCourse(courseId);
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

:root {
  --primary-color: #00a1d6;
  --dark-primary: #0087b3;
  --secondary-color: #f25d8e;
  --light-bg: #f4f5f7;
  --dark-text: #18191c;
  --gray-text: #9499a0;
  --border-color: #e3e5e7;
  --card-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

body {
  background-color: var(--light-bg);
  color: var(--dark-text);
  padding-top: 80px;
}

/* 主内容区 */
.container {
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
  padding: 20px;
}

/* 视频播放器区域 */
.video-container {
  margin-top: 60px;
  flex: 1;
  margin-right: 0;
}

.video-player {
  width: 100%;
  background-color: #000;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.video-player video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-info {
  background-color: white;
  border-radius: 12px;
  margin-top: 24px;
  padding: 30px;
  box-shadow: var(--card-shadow);
  overflow: hidden;
}

.video-title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 24px;
  color: var(--dark-text);
  line-height: 1.5;
  letter-spacing: 0.3px;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--border-color);
}

.stats {
  display: flex;
  color: var(--gray-text);
  font-size: 14px;
}

.stats span {
  margin-right: 24px;
  display: flex;
  align-items: center;
}

.stats i {
  margin-right: 6px;
}

.actions button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
}

.actions button:hover {
  background-color: var(--dark-primary);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 161, 214, 0.3);
}

.actions button i {
  margin-right: 8px;
}

.teacher-info {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, rgba(0, 161, 214, 0.05) 0%, rgba(242, 93, 142, 0.05) 100%);
  padding: 20px 24px;
  border-radius: 10px;
  margin-left: -30px;
  margin-right: -30px;
}

.teacher-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  margin-right: 18px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 161, 214, 0.2);
}

.teacher-details {
  flex: 1;
}

.teacher-details h3 {
  font-size: 18px;
  margin-bottom: 4px;
  color: var(--dark-text);
  font-weight: 600;
}

.teacher-details p {
  color: var(--gray-text);
  font-size: 14px;
}

.video-description {
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  padding: 24px;
  border-radius: 12px;
  font-size: 15px;
  line-height: 2;
  color: #4a4a4a;
  border-left: 4px solid var(--primary-color);
}

.video-description p {
  margin: 0;
  padding: 0;
}

.video-description * {
  font-size: inherit !important;
  line-height: inherit !important;
  color: inherit !important;
}

.video-description h4 {
  color: var(--dark-text) !important;
  font-weight: 600 !important;
  margin-top: 16px !important;
  margin-bottom: 12px !important;
  font-size: 16px !important;
}

.video-description ul,
.video-description ol {
  padding-left: 24px !important;
  margin: 12px 0 !important;
}

.video-description li {
  margin-bottom: 8px !important;
}
</style>
